import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import './styles.scss';

// ── Toast helper ──────────────────────────────────────────────────────────────
const Toast = ({ message, visible }) => (
  <div className={`td-toast${visible ? ' show' : ''}`}>
    <svg style={{ width: 18, height: 18, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
    {message}
  </div>
);

// ── Delete icon ───────────────────────────────────────────────────────────────
const DeleteIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const INITIAL_ROWS = [
  { id: 'doc-0', category: 'Product Master Data', categorySpan: 1, file: 'DASI_Master_882.xlsx',       status: '115 Parts Mapped; Missing 0', pct: 100, error: false, deleted: false },
  { id: 'doc-1', category: 'Commercial Invoices', categorySpan: 2, file: 'Inv_CN_02152026_882.pdf',    status: 'Mapped 1054; Missing 14',    pct: 98,  error: false, deleted: false },
  { id: 'doc-2', category: null,                  categorySpan: 0, file: 'Inv_CN_02262026_883.pdf',    status: 'Mapped 2081; Missing 0',     pct: 100, error: false, deleted: false },
  { id: 'doc-3', category: 'Transport (BOL/AWB)', categorySpan: 1, file: 'AWB_884.pdf',                status: 'Missing Date of Export',     pct: 0,   error: true,  deleted: false },
];

const DataIngestion = () => {
  const navigate = useNavigate();

  // Upload zones
  const [uploads, setUploads] = useState({ master: false, ci: false, transport: false, broker: false });

  // Extraction verification table
  const [rows, setRows] = useState(INITIAL_ROWS);

  // Modals
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [fixDate, setFixDate] = useState('');
  const [dateError, setDateError] = useState(false);

  // Toast
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimer = useRef(null);

  const showToast = (msg) => {
    setToastMsg(msg);
    setToastVisible(true);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastVisible(false), 3500);
  };

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const handleUpload = (key) => setUploads((p) => ({ ...p, [key]: true }));

  const handleDelete = (id, label) => {
    if (window.confirm('Are you sure you want to delete this data? This action cannot be undone.')) {
      setRows((prev) => prev.map((r) => r.id === id ? { ...r, deleted: true } : r));
      showToast(label + ' deleted.');
    }
  };

  const handleSaveCorrection = () => {
    if (!fixDate) { setDateError(true); return; }
    setDateError(false);
    setSplitModalOpen(false);
    setRows((prev) =>
      prev.map((r) => r.id === 'doc-3'
        ? { ...r, error: false, status: 'Export Date Verified (Manual)', pct: 100 }
        : r
      )
    );
    showToast('Pipeline re-validated successfully.');
  };

  const handleCollate = () => {
    showToast('3 Entry Packets successfully collated and ready for Manager review.');
    setTimeout(() => navigate('/pre-audit-verification'), 1000);
  };

  const visibleRows = rows.filter((r) => !r.deleted);

  return (
    <Box className="td-ingestion-wrapper">
      <Toast message={toastMsg} visible={toastVisible} />

      {/* ── Upload Zones ────────────────────────────────────────────────── */}
      <h3 className="td-section-title">Document Control</h3>
      <p className="td-section-sub">Upload your internal compliance instructions and raw supply chain documents.</p>

      <div className="td-drop-grid">
        {[
          { key: 'master',    label: '1. Corporate Instructions',   sub: 'Product Master & Target Matrix' },
          { key: 'ci',        label: '2. Commercial Invoices',      sub: 'Raw Financial Data' },
          { key: 'transport', label: '3. Transport (BOL/AWB)',      sub: 'Export Date & Manifest' },
          { key: 'broker',    label: '4. Broker 7501s',             sub: 'Filed Customs Entries' },
        ].map(({ key, label, sub }) => (
          <div
            key={key}
            className={`td-drop-zone${uploads[key] ? ' td-drop-uploaded' : ''}`}
            onClick={() => handleUpload(key)}
          >
            <h3>{label}</h3>
            <p>{sub}</p>
            {uploads[key]
              ? <span style={{ color: '#166534', fontWeight: 700, fontSize: 13 }}>✓ Uploaded</span>
              : <button className="td-btn-upload" onClick={(e) => { e.stopPropagation(); handleUpload(key); }}>Browse Files</button>
            }
          </div>
        ))}
      </div>

      {/* ── Extraction Verification Table ───────────────────────────────── */}
      <h3 className="td-section-title">Extraction Verification (Staging)</h3>
      <p className="td-section-sub">Document-level pipeline status. Resolve extraction errors before batch collation.</p>

      <div className="td-card" style={{ background: 'white', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <table className="td-data-table">
          <thead>
            <tr>
              <th>Document Type</th>
              <th>Source File</th>
              <th>Extraction Status</th>
              <th>Completeness</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
                <tr key={row.id} className={row.error ? 'td-row-danger' : ''}>
                  {row.categorySpan > 0 && (
                    <td rowSpan={row.categorySpan} className="td-category-cell">{row.category}</td>
                  )}
                  <td style={{ fontFamily: 'monospace', color: '#64748b' }}>{row.file}</td>
                  <td>
                    {row.error ? (
                      <strong style={{ color: '#ef4444' }}>{row.status}</strong>
                    ) : row.status}
                  </td>
                  <td>
                    <span className={`td-dot ${row.pct === 100 ? 'green' : row.pct >= 90 ? 'yellow' : 'red'}`} />
                    {row.pct}%
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {row.error
                        ? <button className="td-btn-text" onClick={() => setSplitModalOpen(true)}>View & Edit</button>
                        : <button className="td-btn-text" onClick={() => setReviewModalOpen(true)}>View & Edit</button>
                      }
                      <button
                        className="td-btn-icon"
                        title="Delete file"
                        onClick={() => handleDelete(row.id, `File ${row.file}`)}
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        <div className="td-footer-action">
          <span style={{ color: '#64748b', fontSize: 13 }}>Resolve critical errors before collating batch.</span>
          <button className="td-primary-btn" onClick={handleCollate}>
            Collate Entry Packets →
          </button>
        </div>
      </div>

      {/* ── Split Modal: Resolve OCR Error ──────────────────────────────── */}
      <div className={`td-modal-overlay${splitModalOpen ? ' open' : ''}`}>
        <div className="td-modal-split">

          {/* Left: PDF Mockup */}
          <div className="td-pane-left">
            <div className="td-pdf-header">
              <span>Source Document: <strong>AWB_884.pdf</strong></span>
              <button className="td-btn-outline" style={{ background: 'transparent', color: 'white', borderColor: '#475569' }}>
                Upload Replacement PDF
              </button>
            </div>
            <div className="td-pdf-mockup">
              <div className="td-pdf-page">
                <div className="td-pdf-page-header">
                  <div>
                    <h1 style={{ fontSize: 22, fontWeight: 'bold', letterSpacing: 1, margin: 0 }}>AIR WAYBILL</h1>
                    <div style={{ fontSize: 12 }}>Not Negotiable</div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 14 }}><strong>AWB: 409-2234</strong></div>
                </div>

                <div className="td-pdf-grid">
                  <div className="td-pdf-box">
                    <strong>Shipper's Name and Address</strong>
                    DASI AEROSPACE<br />
                    1-1-1 MARUNOUCHI, CHIYODA-KU<br />
                    TOKYO 100-0005, JAPAN
                  </div>
                  <div className="td-pdf-box">
                    <strong>Consignee's Name and Address</strong>
                    DASI USA HQ<br />
                    14500 LAX BLVD<br />
                    LOS ANGELES, CA 90045
                  </div>
                </div>

                <div className="td-pdf-grid">
                  <div className="td-pdf-box">
                    <strong>Issuing Carrier's Agent</strong>
                    NIPPON EXPRESS
                  </div>
                  <div className="td-pdf-box">
                    <strong>Accounting Information</strong>
                    FREIGHT PREPAID
                  </div>
                  <div className="td-pdf-box">
                    <strong>Airport of Departure</strong>
                    SHANGHAI (PVG)
                  </div>
                  <div className="td-pdf-box td-pdf-error-box">
                    <strong style={{ color: '#ef4444' }}>Flight Date / Date of Export</strong>
                    <span className="td-blur-text">2026-02-20 / ILLEGIBLE</span>
                  </div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20 }}>
                  <thead>
                    <tr>
                      {['No. of Pieces', 'Gross Weight', 'Commodity Item No.', 'Nature and Quantity'].map((h) => (
                        <th key={h} style={{ border: '1px solid #111', padding: '5px 8px', background: '#f9f9f9', fontSize: 11 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ border: '1px solid #111', padding: '5px 8px', fontSize: 11 }}>14 Pallets</td>
                      <td style={{ border: '1px solid #111', padding: '5px 8px', fontSize: 11 }}>2,450 KGS</td>
                      <td style={{ border: '1px solid #111', padding: '5px 8px', fontSize: 11 }}>8803.30</td>
                      <td style={{ border: '1px solid #111', padding: '5px 8px', fontSize: 11 }}>AEROSPACE ACTUATOR ASSEMBLIES AND LANDING STRUT COMPONENTS.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right: Fix Form */}
          <div className="td-pane-right">
            <h2 style={{ marginTop: 0, fontSize: 18 }}>Resolve Extraction Error</h2>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>
              Review and correct the missing data highlighted to proceed.
            </p>

            <div className="td-form-group">
              <label>Manifest / AWB Number</label>
              <input type="text" value="409-2234" readOnly style={{ background: '#f1f5f9', color: '#64748b' }} />
            </div>
            <div className="td-form-group">
              <label>Port of Lading</label>
              <input type="text" defaultValue="SHANGHAI" />
            </div>
            <div className="td-form-group">
              <label>
                Date of Export{' '}
                <span style={{ color: '#ef4444' }}>*Required</span>
              </label>
              <input
                type="date"
                className={dateError ? 'td-input-error' : ''}
                value={fixDate}
                onChange={(e) => { setFixDate(e.target.value); setDateError(false); }}
              />
              {dateError && (
                <span className="td-error-msg">OCR failed to read. Critical for FX calculation.</span>
              )}
            </div>

            <div style={{ marginTop: 'auto', paddingTop: 24 }}>
              <button
                className="td-primary-btn"
                style={{ width: '100%' }}
                onClick={handleSaveCorrection}
              >
                Save &amp; Re-Validate Pipeline
              </button>
              <button
                className="td-btn-text"
                style={{ width: '100%', marginTop: 12, color: '#64748b', textAlign: 'center', display: 'block' }}
                onClick={() => { setSplitModalOpen(false); setDateError(false); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Review Modal: Read-only View ────────────────────────────────── */}
      <div className={`td-modal-overlay${reviewModalOpen ? ' open' : ''}`}>
        <div className="td-modal-split">
          <div className="td-pane-left">
            <div className="td-pdf-header">
              <span>Source Document: <strong>Inv_CN_02262026_883.pdf</strong></span>
            </div>
            <div className="td-pdf-mockup" style={{ alignItems: 'flex-start' }}>
              <div className="td-pdf-page" style={{ maxWidth: 500 }}>
                <div style={{ border: '2px solid #10b981', background: 'rgba(16,185,129,0.1)', padding: 16, borderRadius: 4 }}>
                  <strong>Commercial Invoice #883</strong>
                  <div style={{ marginTop: 10, fontSize: 12 }}>Total Value: $45,200.00<br />Incoterm: FOB</div>
                </div>
              </div>
            </div>
          </div>
          <div className="td-pane-right">
            <h2 style={{ marginTop: 0, fontSize: 18 }}>Extraction Review</h2>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>Data successfully extracted and mapped. Read-only view.</p>

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 16 }}>
              {[
                { label: 'Invoice Number', value: 'INV-883' },
                { label: 'Total Value',    value: '$45,200.00' },
                { label: 'Incoterm',       value: 'FOB' },
              ].map(({ label, value }, i, arr) => (
                <div key={label} style={{
                  display: 'flex', justifyContent: 'space-between',
                  borderBottom: i < arr.length - 1 ? '1px solid #e2e8f0' : 'none',
                  paddingBottom: 8, marginBottom: i < arr.length - 1 ? 8 : 0,
                }}>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{value}</span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 'auto', paddingTop: 24 }}>
              <button className="td-primary-btn" style={{ width: '100%' }} onClick={() => setReviewModalOpen(false)}>Done</button>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default DataIngestion;

